<!-- eslint-disable vue/html-self-closing -->
<template>
	<nav class="nav" :class="{ 'nav--minimized': isMinimized }">
		<div class="nav__container">
			<!-- Logo -->
			<NuxtLink to="/" class="nav__logo" aria-label="Baghera Advisory - Retour à l'accueil">
				<img src="/img/logo-horizontal-baghera.svg" alt="Baghera Advisory" />
			</NuxtLink>

			<!-- Desktop menu -->
			<div class="nav__menu-desktop">
				<NuxtLink
					v-for="item in menuItems"
					:key="item.to"
					:to="item.to"
					class="nav__link"
					:class="{ 'nav__link--active': item.isActive?.(route.path) }"
				>
					{{ item.label }}
				</NuxtLink>
			</div>

			<!-- Burger button (mobile) -->
			<button
				class="nav__burger"
				:class="{ 'nav__burger--open': isMobileMenuOpen }"
				@click="toggleMobileMenu"
				aria-label="Toggle navigation menu"
				:aria-expanded="isMobileMenuOpen"
			>
				<span></span>
				<span></span>
				<span></span>
			</button>
		</div>

		<!-- Mobile menu overlay -->
		<Transition name="slide-down">
			<div v-if="isMobileMenuOpen" class="mobile-menu">
				<div class="mobile-menu__content">
					<NuxtLink
						v-for="item in menuItems"
						:key="item.to"
						:to="item.to"
						class="mobile-menu__link"
						@click="closeMobileMenu"
					>
						{{ item.label }}
					</NuxtLink>
				</div>
			</div>
		</Transition>
	</nav>
</template>

<script setup lang="ts">
defineOptions({
	name: 'BagheraNavigation',
})

const route = useRoute()

const menuItems = [
	{ to: '/', label: 'Accueil', isActive: (path: string) => path === '/' },
	{ to: '/domaine-de-competences', label: 'Nos compétences', isActive: (path: string) => path === '/domaine-de-competences' },
	{ to: '/#apropos', label: 'À propos' },
	{ to: '/contact', label: 'Contact', isActive: (path: string) => path === '/contact' },
]

const isMinimized = ref(false)
const isMobileMenuOpen = ref(false)

const handleScroll = () => {
	isMinimized.value = window.scrollY > 50
}

const toggleMobileMenu = () => {
	isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
	isMobileMenuOpen.value = false
}

// Manage body overflow when mobile menu state changes
watch(isMobileMenuOpen, (isOpen) => {
	document.body.style.overflow = isOpen ? 'hidden' : ''
})

// Close mobile menu on route change
watch(() => route.fullPath, () => {
	if (isMobileMenuOpen.value) {
		closeMobileMenu()
	}
})

onMounted(() => {
	window.addEventListener('scroll', handleScroll, { passive: true })
	handleScroll()
})

onUnmounted(() => {
	window.removeEventListener('scroll', handleScroll)
	document.body.style.overflow = ''
})
</script>

<style scoped lang="postcss">
.nav {
	@apply fixed top-0 left-0 right-0 z-50
	transition-all duration-300 ease-in-out;
	height: 100px;
	background: linear-gradient(to bottom, rgba(11, 22, 35, 0.6), transparent);
}

.nav--minimized {
	height: 70px;
	@apply backdrop-blur-sm shadow-lg;
	background-color: rgba(11, 22, 35, 0.95);
}

.nav__container {
	@apply flex items-center justify-between h-full px-6
	laptop:px-12 laptop:max-w-[1440px] laptop:mx-auto;
}

.nav__logo {
	@apply transition-all duration-300;

	img {
		@apply h-10 w-auto transition-all duration-300;
	}
}

.nav--minimized .nav__logo img {
	@apply h-8;
}

/* Desktop menu */
.nav__menu-desktop {
	@apply hidden laptop:flex items-center gap-8;
}

.nav__link {
	@apply text-tertiary text-base font-medium
	transition-colors duration-300
	hover:text-secondary;
	text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.nav__link--active {
	@apply text-secondary;
}

/* Burger button */
.nav__burger {
	@apply flex flex-col justify-center items-center gap-1.5 w-10 h-10 cursor-pointer
	laptop:hidden;

	span {
		@apply block w-6 h-0.5 bg-tertiary transition-all duration-300 origin-center;
	}

	&--open {
		span:nth-child(1) {
			@apply translate-y-2 rotate-45;
		}
		span:nth-child(2) {
			@apply opacity-0;
		}
		span:nth-child(3) {
			@apply -translate-y-2 -rotate-45;
		}
	}
}

/* Mobile menu overlay */
.mobile-menu {
	@apply fixed left-0 right-0 backdrop-blur-md
	laptop:hidden;
	top: 0;
	height: 100vh;
	z-index: -1;
	background-color: rgba(11, 22, 35, 0.98);
}

.mobile-menu__content {
	@apply flex flex-col items-center justify-center gap-10 h-full;
}

.mobile-menu__link {
	@apply text-tertiary text-2xl font-light
	transition-colors duration-300
	hover:text-secondary;
}

/* Slide down transition */
.slide-down-enter-active,
.slide-down-leave-active {
	@apply transition-transform duration-300 ease-in-out;
}

.slide-down-enter-from,
.slide-down-leave-to {
	transform: translateY(-100%);
}

.slide-down-enter-to,
.slide-down-leave-from {
	transform: translateY(0);
}
</style>
